import { Component, ComponentType } from "@/types";
import type { BaseChartProperties, ChartStyleOverrides } from "@/types/chart-properties";
import { chartTypeRegistry } from "../ChartTypeRegistry";
import { chartDataManager, ChartDataPoint } from "../ChartDataManager";

export interface ChartPropertyChange {
  properties?: Partial<BaseChartProperties>;
  style?: ChartStyleOverrides;
  data?: ChartDataPoint[];
  type?: ComponentType; // For chart type conversion
}

export interface ChartCommand {
  command: string;
  confidence: number;
  category: "data" | "style" | "config" | "interaction" | "conversion";
}

/**
 * Common chart command handler for properties shared across all chart types
 */
export class CommonChartHandler {
  /**
   * Parse common chart commands that apply to all chart types
   */
  parseCommand(command: string, component: Component): ChartPropertyChange | null {
    const lowerCommand = command.toLowerCase();
    const changes: ChartPropertyChange = {};

    // Title and labels
    if (this.isTitleCommand(lowerCommand)) {
      const titleChanges = this.parseTitleCommand(command);
      if (titleChanges) {
        changes.properties = { ...changes.properties, ...titleChanges };
      }
    }

    // Colors
    if (this.isColorCommand(lowerCommand)) {
      const colorChanges = this.parseColorCommand(command, component);
      if (colorChanges) {
        changes.properties = { ...changes.properties, ...colorChanges };
      }
    }

    // Legend
    if (this.isLegendCommand(lowerCommand)) {
      const legendChanges = this.parseLegendCommand(command);
      if (legendChanges) {
        changes.properties = { ...changes.properties, ...legendChanges };
      }
    }

    // Grid
    if (this.isGridCommand(lowerCommand)) {
      const gridChanges = this.parseGridCommand(command);
      if (gridChanges) {
        changes.properties = { ...changes.properties, ...gridChanges };
      }
    }

    // Animation
    if (this.isAnimationCommand(lowerCommand)) {
      const animationChanges = this.parseAnimationCommand(command);
      if (animationChanges) {
        changes.properties = { ...changes.properties, ...animationChanges };
      }
    }

    // Tooltip
    if (this.isTooltipCommand(lowerCommand)) {
      const tooltipChanges = this.parseTooltipCommand(command);
      if (tooltipChanges) {
        changes.properties = { ...changes.properties, ...tooltipChanges };
      }
    }

    // Axes (for cartesian charts)
    if (this.isAxisCommand(lowerCommand) && this.isCartesianChart(component.type)) {
      const axisChanges = this.parseAxisCommand(command);
      if (axisChanges) {
        changes.properties = { ...changes.properties, ...axisChanges };
      }
    }

    // Data operations
    const currentData = Array.isArray(component.properties?.data) ? component.properties.data : [];
    const dataOperation = chartDataManager.parseDataCommand(command, currentData);
    if (dataOperation) {
      const newData = chartDataManager.applyDataOperation(
        currentData,
        dataOperation
      );
      changes.data = newData;
    }

    // Chart type conversion
    if (this.isConversionCommand(lowerCommand)) {
      const newType = this.parseConversionCommand(command, component.type);
      if (newType && newType !== component.type) {
        changes.type = newType;
        // Transform data if needed
        if (component.properties?.data && Array.isArray(component.properties.data)) {
          changes.data = chartDataManager.transformDataForChartType(
            component.properties.data,
            component.type,
            newType
          );
        }
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isTitleCommand(command: string): boolean {
    return /title|heading|caption|subtitle|description/.test(command);
  }

  private parseTitleCommand(command: string): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};

    // Set title
    const titleMatch = command.match(/(?:title|heading)[:\s]+["']([^"']+)["']/i) ||
                       command.match(/(?:set|change|update)\s+title\s+to\s+["']([^"']+)["']/i) ||
                       command.match(/title\s+["']([^"']+)["']/i);
    if (titleMatch) {
      changes.title = titleMatch[1];
    }

    // Set description/subtitle
    const descMatch = command.match(/(?:description|subtitle)[:\s]+["']([^"']+)["']/i) ||
                     command.match(/(?:set|change|update)\s+(?:description|subtitle)\s+to\s+["']([^"']+)["']/i);
    if (descMatch) {
      changes.description = descMatch[1];
    }

    // Remove title
    if (command.includes("remove title") || command.includes("hide title")) {
      changes.title = "";
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isColorCommand(command: string): boolean {
    return /color|colour|theme|palette|style/.test(command);
  }

  private parseColorCommand(command: string, component: Component): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};
    const currentColors = Array.isArray(component.properties?.colors)
      ? component.properties.colors
      : ["#8884d8", "#82ca9d", "#ffc658"];

    // Single color change
    const colorMatch = command.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g);
    if (colorMatch) {
      changes.colors = colorMatch;
      return changes;
    }

    // Named colors
    const colorMap: Record<string, string[]> = {
      // Single colors
      "blue": ["#3B82F6", "#60A5FA", "#93C5FD"],
      "red": ["#EF4444", "#F87171", "#FCA5A5"],
      "green": ["#10B981", "#34D399", "#6EE7B7"],
      "purple": ["#8B5CF6", "#A78BFA", "#C4B5FD"],
      "orange": ["#F97316", "#FB923C", "#FDBA74"],
      "yellow": ["#F59E0B", "#FCD34D", "#FDE68A"],
      "pink": ["#EC4899", "#F472B6", "#F9A8D4"],
      "gray": ["#6B7280", "#9CA3AF", "#D1D5DB"],
      "grey": ["#6B7280", "#9CA3AF", "#D1D5DB"],
      // Themes
      "rainbow": ["#FF0000", "#FF7F00", "#FFFF00", "#00FF00", "#0000FF", "#4B0082", "#9400D3"],
      "pastel": ["#FFB3BA", "#FFDFBA", "#FFFFBA", "#BAFFC9", "#BAE1FF"],
      "earth": ["#8D6E63", "#A1887F", "#BCAAA4", "#D7CCC8"],
      "ocean": ["#0077BE", "#0099CC", "#00BBDD", "#00DDDD"],
      "forest": ["#228B22", "#32CD32", "#90EE90", "#98FB98"],
      "sunset": ["#FF6B6B", "#FF8E53", "#FFB347", "#FFD93D"],
      "monochrome": ["#000000", "#404040", "#808080", "#C0C0C0"],
      "professional": ["#2C3E50", "#34495E", "#7F8C8D", "#95A5A6"],
      "vibrant": ["#FF006E", "#FB5607", "#FFBE0B", "#8338EC", "#3A86FF"],
      // Agricultural themes
      "agricultural": ["#8BC34A", "#689F38", "#558B2F", "#33691E"],
      "crop": ["#FDD835", "#8BC34A", "#43A047", "#795548"],
      "soil": ["#8D6E63", "#6D4C41", "#5D4037", "#4E342E"],
    };

    // Check for color theme
    for (const [themeName, colors] of Object.entries(colorMap)) {
      if (command.toLowerCase().includes(themeName)) {
        changes.colors = colors;
        return changes;
      }
    }

    // Specific color positions
    const positionMatch = command.match(/(?:first|second|third|1st|2nd|3rd)\s+(?:color|line|bar)\s+(\w+)/i);
    if (positionMatch) {
      const colorName = positionMatch[1].toLowerCase();
      const namedColors: Record<string, string> = {
        "blue": "#3B82F6",
        "red": "#EF4444",
        "green": "#10B981",
        "purple": "#8B5CF6",
        "orange": "#F97316",
        "yellow": "#F59E0B",
        "pink": "#EC4899",
        "gray": "#6B7280",
        "grey": "#6B7280",
      };

      if (namedColors[colorName]) {
        const newColors = [...currentColors];
        const position = command.includes("first") || command.includes("1st") ? 0 :
                        command.includes("second") || command.includes("2nd") ? 1 : 2;
        newColors[position] = namedColors[colorName];
        changes.colors = newColors;
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isLegendCommand(command: string): boolean {
    return /legend|key/.test(command);
  }

  private parseLegendCommand(command: string): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};

    if (command.includes("show legend") || command.includes("display legend") || command.includes("add legend")) {
      changes.showLegend = true;
    } else if (command.includes("hide legend") || command.includes("remove legend") || command.includes("no legend")) {
      changes.showLegend = false;
    }

    // Legend position (if we add this feature)
    if (command.includes("legend")) {
      if (command.includes("top")) changes.legendPosition = "top";
      else if (command.includes("bottom")) changes.legendPosition = "bottom";
      else if (command.includes("left")) changes.legendPosition = "left";
      else if (command.includes("right")) changes.legendPosition = "right";
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isGridCommand(command: string): boolean {
    return /grid|gridline/.test(command);
  }

  private parseGridCommand(command: string): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};

    if (command.includes("show grid") || command.includes("display grid") || command.includes("add grid")) {
      changes.showGrid = true;
    } else if (command.includes("hide grid") || command.includes("remove grid") || command.includes("no grid")) {
      changes.showGrid = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isAnimationCommand(command: string): boolean {
    return /animat|transition|effect/.test(command);
  }

  private parseAnimationCommand(command: string): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};

    if (command.includes("enable animation") || command.includes("add animation") || command.includes("animate")) {
      changes.animation = true;
    } else if (command.includes("disable animation") || command.includes("no animation") || command.includes("remove animation")) {
      changes.animation = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isTooltipCommand(command: string): boolean {
    return /tooltip|hover|popup/.test(command);
  }

  private parseTooltipCommand(command: string): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};

    if (command.includes("show tooltip") || command.includes("enable tooltip") || command.includes("add tooltip")) {
      changes.showTooltip = true;
    } else if (command.includes("hide tooltip") || command.includes("disable tooltip") || command.includes("no tooltip")) {
      changes.showTooltip = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isAxisCommand(command: string): boolean {
    return /axis|axes|label/.test(command);
  }

  private parseAxisCommand(command: string): Partial<BaseChartProperties> | null {
    const changes: Partial<BaseChartProperties> = {};
    const lowerCommand = command.toLowerCase();

    // Handle axis visibility commands first (most specific)
    if (lowerCommand.includes("remove ax") || lowerCommand.includes("hide ax") || lowerCommand.includes("no ax")) {
      // Both axes
      if (lowerCommand.includes("axes") || lowerCommand.includes("both") ||
          lowerCommand.includes("all") || lowerCommand.includes("x and y")) {
        changes.showXAxis = false;
        changes.showYAxis = false;
      }
      // X axis only
      else if (lowerCommand.includes("x ax") || lowerCommand.includes("horizontal")) {
        changes.showXAxis = false;
      }
      // Y axis only
      else if (lowerCommand.includes("y ax") || lowerCommand.includes("vertical")) {
        changes.showYAxis = false;
      }
      // Default to both if not specified
      else {
        changes.showXAxis = false;
        changes.showYAxis = false;
      }
    }

    // Handle show axis commands
    if (lowerCommand.includes("show ax") || lowerCommand.includes("display ax") || lowerCommand.includes("add ax")) {
      // Both axes
      if (lowerCommand.includes("axes") || lowerCommand.includes("both") ||
          lowerCommand.includes("all") || lowerCommand.includes("x and y")) {
        changes.showXAxis = true;
        changes.showYAxis = true;
      }
      // X axis only
      else if (lowerCommand.includes("x ax") || lowerCommand.includes("horizontal")) {
        changes.showXAxis = true;
      }
      // Y axis only
      else if (lowerCommand.includes("y ax") || lowerCommand.includes("vertical")) {
        changes.showYAxis = true;
      }
      // Default to both if not specified
      else {
        changes.showXAxis = true;
        changes.showYAxis = true;
      }
    }

    // X-axis label
    const xLabelMatch = command.match(/x[\s-]?axis\s+label[:\s]+["']([^"']+)["']/i) ||
                       command.match(/horizontal\s+axis[:\s]+["']([^"']+)["']/i);
    if (xLabelMatch) {
      changes.xAxisLabel = xLabelMatch[1];
    }

    // Y-axis label
    const yLabelMatch = command.match(/y[\s-]?axis\s+label[:\s]+["']([^"']+)["']/i) ||
                       command.match(/vertical\s+axis[:\s]+["']([^"']+)["']/i);
    if (yLabelMatch) {
      changes.yAxisLabel = yLabelMatch[1];
    }

    // Axis types
    if (command.includes("logarithmic") || command.includes("log scale")) {
      if (command.includes("x")) {
        changes.xAxisType = "log";
      } else if (command.includes("y")) {
        changes.yAxisType = "log";
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isConversionCommand(command: string): boolean {
    return /convert|change.*to|transform|switch.*to|make.*it/.test(command);
  }

  private parseConversionCommand(command: string, currentType: ComponentType): ComponentType | null {
    const chartTypes: Record<string, ComponentType> = {
      "line": ComponentType.LINE_CHART,
      "bar": ComponentType.BAR_CHART,
      "pie": ComponentType.PIE_CHART,
      "area": ComponentType.AREA_CHART,
      "scatter": ComponentType.SCATTER_CHART,
      "column": ComponentType.BAR_CHART,
      "donut": ComponentType.PIE_CHART,
    };

    // Look for "to [chart type]" pattern to identify target chart type
    const toPattern = /to\s+(?:a\s+)?(\w+)\s*(?:chart)?/i;
    const match = command.match(toPattern);

    if (match) {
      const targetType = match[1].toLowerCase();

      // Check if the target type matches any chart type
      for (const [keyword, type] of Object.entries(chartTypes)) {
        if (targetType.includes(keyword) || keyword.includes(targetType)) {
          // Skip if trying to convert to the same type
          if (type === currentType) {
            continue;
          }
          // Check if conversion is allowed
          if (chartTypeRegistry.canConvertTo(currentType, type)) {
            return type;
          }
        }
      }
    }

    // Fallback to simpler keyword matching
    for (const [keyword, type] of Object.entries(chartTypes)) {
      if (command.toLowerCase().includes(keyword)) {
        // Skip if trying to convert to the same type
        if (type === currentType) {
          continue;
        }
        // Check if conversion is allowed
        if (chartTypeRegistry.canConvertTo(currentType, type)) {
          return type;
        }
      }
    }

    return null;
  }

  private isCartesianChart(type: ComponentType): boolean {
    return [
      ComponentType.LINE_CHART,
      ComponentType.BAR_CHART,
      ComponentType.AREA_CHART,
      ComponentType.SCATTER_CHART,
    ].includes(type);
  }

  /**
   * Get command suggestions based on current chart state
   */
  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [];
    const hasData = component.properties?.data &&
      Array.isArray(component.properties.data) &&
      component.properties.data.length > 0;

    // Data suggestions
    if (!hasData) {
      suggestions.push("Add sample data", "Generate monthly data", "Import data from clipboard");
    } else {
      suggestions.push("Add more data points", "Remove last data point", "Clear all data");
    }

    // Title suggestions
    if (!component.properties?.title) {
      suggestions.push('Add title "Sales Report"', 'Set chart title');
    }

    // Style suggestions
    if (component.properties?.showLegend !== false) {
      suggestions.push("Hide legend");
    } else {
      suggestions.push("Show legend");
    }

    if (component.properties?.showGrid !== false) {
      suggestions.push("Hide grid lines");
    } else {
      suggestions.push("Show grid lines");
    }

    // Color suggestions
    suggestions.push("Use blue theme", "Apply rainbow colors", "Make it professional");

    // Chart type conversion
    const relatedTypes = chartTypeRegistry.getRelatedTypes(component.type);
    relatedTypes.forEach(type => {
      const config = chartTypeRegistry.getConfig(type);
      if (config) {
        suggestions.push(`Convert to ${config.name.toLowerCase()}`);
      }
    });

    return suggestions;
  }
}

export const commonChartHandler = new CommonChartHandler();
